import { combineReducers } from 'redux';
import { routerReducer } from 'react-router-redux';
// Models
import mProduct from '../models/product';

// ------------------------------------- //
// Common reducers
// ------------------------------------- //
import settings from './app/settings';
import version from './app/version';
import persist from './app/persist';
import intl from './app/intl';
import loading from './app/loading';
import webSocket from './webSocket/webSocket';
import notification from './app/notification';
import dialog from './app/dialog';
import country from './app/country';
import list from './app/list/list';
import pageSize from './app/list/pageSize';
import auth from './auth/auth';
import me from './auth/me';

// ------------------------------------- //
// Specific OPAC reducers
// ------------------------------------- //
import autoComplete from './autoComplete/autoComplete';
import listFilter from './listFilter/listFilter';
import asyncOperation from './asyncOperation/asyncOperation';
// Device data
import deviceData from './deviceData/deviceData';
// Device events
import deviceEvent from './deviceEvent/deviceEvent';
// Operators
import operator from './operator/operator';
// Equipments
import equipment from './equipment/equipment';
// Device models
import deviceModel from './device/deviceModel';
// Devices
import device from './device/device';
import deviceConfig from './device/deviceConfig';
import deviceLog from './device/deviceLog';
import deviceCmd from './device/deviceCmd';
import deviceSms from './device/deviceSms';
import deviceDistribution from './device/deviceDistribution';
// Device slaves
import deviceSlave from './device/deviceSlave';
// Installations
import installation from './installation/installation';
import installationEntity from './installation/installationEntity';
import installationDoor from './installation/installationDoor';
import installationSource from './installation/installationSource';
import installationDevice from './installation/installationDevice';
import installationDeviceSlave from './installation/installationDeviceSlave';
import installationScenario from './installation/installationScenario';
import installationCustomer from './installation/installationCustomer';
import installationCustomerAccessway from './installation/installationCustomerAccessway';
import installationPlanning from './installation/installationPlanning';
import installationLog from './installation/installationLog';
import installationTrigger from './installation/installationTrigger';
import installationAsyncOperation from './installation/installationAsyncOperation';
// Simcards
import simcard from './simcard/simcard';
import simcardGroup from './simcard/simcardGroup';
import apn from './simcard/apn';
// Companies
import company from './company/company';
// Users and profiles
import user from './user/user';
import profile from './user/profile';
import permission from './user/permission';
import alerting from './user/alerting';
// Production
import deviceBatch from './production/deviceBatch';
// Updates
import distribution from './updates/distribution';
import rollout from './updates/rollout';

// ------------------------------------- //
// Specific CPM reducers
// ------------------------------------- //
import cpmOperation from './cpm/operation/operation';
import cpmEvent from './cpm/event/event';
import cpmAutoComplete from './cpm/autoComplete/autoComplete';
import cpmListFilter from './cpm/listFilter/listFilter';
// Carparks
import cpmCarpark from './cpm/carpark/carpark';
import cpmCarparkTrigger from './cpm/carpark/carparkTrigger';
import cpmCarparkActivity from './cpm/carpark/carparkActivity';
import cpmCarparkPlanning from './cpm/carpark/carparkPlanning';
import cpmCarparkParkingCategory from './cpm/carpark/carparkParkingCategory';
import cpmCarparkPriceGrid from './cpm/carpark/carparkPriceGrid';
import cpmCarparkCustomer from './cpm/carpark/carparkCustomer';
import cpmCarparkParking from './cpm/carpark/carparkParking';
import cpmCarparkOnDemand from './cpm/carpark/carparkOnDemand';
import cpmCarparkAccess from './cpm/carpark/carparkAccess';
import cpmCarparkMetric from './cpm/carpark/carparkMetric';
import cpmCarparkBooking from './cpm/carpark/carparkBooking';
import cpmCarparkInvoice from './cpm/carpark/carparkInvoice';
import cpmBooking from './cpm/booking/booking';
import cpmSidebar from './cpm/sidebar/sidebar';
import cpmOnDemand from './cpm/onDemand/onDemand';
import cpmPartner from './cpm/partner/partner';
import cpmWorkspace from './cpm/workspace/workspace';
import cpmWorkspaceGroup from './cpm/workspace/workspaceGroup';
import cpmFinance from './cpm/finance/finance';
import { reducer as cpmServiceFeeRule } from './cpm/workspace/serviceFeeRule';
import cpmSlug from './cpm/workspace/slug';

/*
** From Webpack Env Config
*/
const PRODUCT = __PRODUCT__; // eslint-disable-line no-undef

// ------------------------------------- //
// Build reducers to be combined
// ------------------------------------- //
// Common
const commonReducers = {
  routing: routerReducer,
  auth,
  country,
  dialog,
  intl,
  list,
  loading,
  me,
  notification,
  pageSize,
  persist,
  settings,
  version,
  webSocket,
};

// Products
const productsReducers = {
  // OPAC
  [mProduct.list[0].value]: {
    autoComplete,
    listFilter,
    asyncOperation,
    // Device data
    deviceData,
    // Device event
    deviceEvent,
    // Operators
    operator,
    // Equipments
    equipment,
    // Device models
    deviceModel,
    // Devices
    device,
    deviceConfig,
    deviceLog,
    deviceCmd,
    deviceSms,
    deviceDistribution,
    // Device slaves
    deviceSlave,
    // Installations
    installation,
    installationEntity,
    installationDoor,
    installationSource,
    installationDevice,
    installationDeviceSlave,
    installationScenario,
    installationCustomer,
    installationCustomerAccessway,
    installationPlanning,
    installationLog,
    installationTrigger,
    installationAsyncOperation,
    // Simcards
    simcard,
    simcardGroup,
    apn,
    // Companies
    company,
    // Users
    user,
    profile,
    permission,
    alerting,
    // Production
    deviceBatch,
    // Updates
    distribution,
    rollout,
  },
  // CPM manager
  [mProduct.list[1].value]: {
    cpmOperation,
    cpmEvent,
    cpmAutoComplete,
    cpmListFilter,
    // Carparks
    cpmCarpark,
    cpmCarparkTrigger,
    cpmCarparkActivity,
    cpmCarparkPlanning,
    cpmCarparkParkingCategory,
    cpmCarparkPriceGrid,
    cpmCarparkCustomer,
    cpmCarparkParking,
    cpmCarparkOnDemand,
    cpmCarparkAccess,
    cpmCarparkMetric,
    cpmCarparkBooking,
    cpmCarparkInvoice,
    // Sidebar
    cpmSidebar,
    // Account
    cpmPartner,
    cpmFinance,
    // Workspace
    cpmServiceFeeRule,
    cpmSlug,
    cpmWorkspace,
    cpmWorkspaceGroup,
  },
  // CPM client
  [mProduct.list[2].value]: {
    cpmOperation,
    cpmEvent,
    // Booking
    cpmBooking,
    // Carparks
    cpmCarpark,
    // Finance
    cpmFinance,
    // OnDemand
    cpmOnDemand,
    // Slug
    cpmSlug,
  },
};

module.exports = combineReducers({
  ...commonReducers,
  ...productsReducers[PRODUCT],
});



// WEBPACK FOOTER //
// ./reducers/index.js